import { useTranslation } from 'react-i18next';

import SvgTags from '@/assets/widgets/empty-state-tags.svg';
import { TagsWidget } from '@/components/widgets/tags-widget/tags-widget';
import { modalState } from '@/state/modal.state';
import { Header } from '@/ui/typography/widgets';
import { EmptyStateWidget } from '@/ui/widget/empty-state-widget';
import { ReadOnlyEmptyWidget } from '@/ui/widget/read-only-empty-widget';

import { useCanEditCompany } from '../../hooks/use-can-edit-company';
import { useCrunchbaseEnrichmentCheck } from '../../hooks/use-crunchbase-enrichment-check';
import { useCompany } from '../use-company.query';

import { useCompanyTags } from './use-company-tags.query';
import { useSetAtom } from 'jotai';

const COMPANY_TAGS_WIDGET_HEIGHT = 438;

export const CompanyTagsWidget = ({
  className,
  companyId,
  height = COMPANY_TAGS_WIDGET_HEIGHT,
  readonlyFullHeight = true,
  header,
  editable = true,
}: {
  className?: string;
  companyId: string | null;
  height?: number;
  readonlyFullHeight?: boolean;
  header?: React.ReactNode;
  editable?: boolean;
}) => {
  const { t } = useTranslation('companies', { keyPrefix: 'tags' });
  const { isEnrichedFromCrunchbase, lastEditedAt } =
    useCrunchbaseEnrichmentCheck('tags');
  const { data: company } = useCompany(companyId);
  const { data, refetch } = useCompanyTags(companyId);
  const setModal = useSetAtom(modalState);
  const canEditCompany = useCanEditCompany();

  const tags = data?.tags ?? [];

  const onEditClick = () => {
    company &&
      setModal({
        state: 'manageTags',
        type: 'company',
        entityId: company.id,
        refetchTags: refetch,
      });
  };

  if (!tags.length && canEditCompany && editable) {
    return (
      <EmptyStateWidget
        className={className}
        height={height}
        dataTestId="tags-widget"
        image={SvgTags}
        label={t`header`}
        linkText={t`button`}
        onEditClick={onEditClick}
        direction="column"
      />
    );
  }
  if (!tags.length)
    return (
      <ReadOnlyEmptyWidget
        height={height}
        header={header ?? <Header>{t`tags`}</Header>}
        editable={editable}
        fullHeight={readonlyFullHeight}
        className={className}
      />
    );

  return (
    <TagsWidget
      className={className}
      testId="tags-widget"
      disabled={!canEditCompany}
      onEditClick={onEditClick}
      tags={tags}
      widgetHeight={COMPANY_TAGS_WIDGET_HEIGHT}
      header={header}
      showEditStamp={isEnrichedFromCrunchbase}
      editable={editable}
      editStampProps={{
        crunchbasePermalink: company?.crunchbasePermalink ?? null,
        lastEdited: lastEditedAt,
      }}
    />
  );
};
